/**
 * Severity types and constants for the Event Aggregator system
 *
 * Severity is a 1-10 numeric scale used to classify the urgency of measurements and events:
 * - Routine (1-2): Normal activity (foot traffic, routine maintenance, standard operations)
 * - Notable (3-4): Notable but not concerning (unusual activity, delivery, visitor, minor anomaly)
 * - Concern (5-6): Safety or security concern (person falling, unauthorized access, suspicious behavior)
 * - Serious (7-8): Serious concern requiring attention (injury, trespassing, theft attempt, aggressive behavior)
 * - Critical (9-10): Critical emergency (weapon detected, active threat, fire, medical emergency)
 */

/**
 * Minimum severity value (routine activity)
 */
export const SEVERITY_MIN = 1;

/**
 * Maximum severity value (critical/emergency)
 */
export const SEVERITY_MAX = 10;

/**
 * Threshold at or above which measurements trigger real-time alerting
 * Measurements with severity >= this value bypass normal aggregation timing
 */
export const SEVERITY_THRESHOLD = 7;

/**
 * Severity level categories for classification
 */
export const SeverityLevel = {
	/** Safety or security concern (5-6) - person falling, unauthorized access, suspicious behavior */
	CONCERN: "concern",
	/** Critical emergency (9-10) - weapon, active threat, fire, medical emergency */
	CRITICAL: "critical",
	/** Notable but not concerning (3-4) - unusual activity, delivery, visitor, minor anomaly */
	NOTABLE: "notable",
	/** Normal activity (1-2) - foot traffic, routine maintenance, standard operations */
	ROUTINE: "routine",
	/** Serious concern requiring attention (7-8) - injury, trespassing, theft, aggression */
	SERIOUS: "serious",
} as const;

export type SeverityLevel = (typeof SeverityLevel)[keyof typeof SeverityLevel];

/**
 * Severity range boundaries for each level
 */
export const SeverityRanges = {
	/** Concern: 5-6 - Person falling, unauthorized access, suspicious behavior */
	[SeverityLevel.CONCERN]: { max: 6, min: 5 },
	/** Critical: 9-10 - Weapon, active threat, fire, medical emergency */
	[SeverityLevel.CRITICAL]: { max: 10, min: 9 },
	/** Notable: 3-4 - Unusual activity, delivery, visitor, minor anomaly */
	[SeverityLevel.NOTABLE]: { max: 4, min: 3 },
	/** Routine: 1-2 - Foot traffic, routine maintenance, standard operations */
	[SeverityLevel.ROUTINE]: { max: 2, min: 1 },
	/** Serious: 7-8 - Injury, trespassing, theft attempt, aggressive behavior */
	[SeverityLevel.SERIOUS]: { max: 8, min: 7 },
} as const;

/**
 * Gets the severity level category for a given severity value
 */
export function getSeverityLevel(severity: number): SeverityLevel {
	if (severity >= SeverityRanges.critical.min) {
		return SeverityLevel.CRITICAL;
	}
	if (severity >= SeverityRanges.serious.min) {
		return SeverityLevel.SERIOUS;
	}
	if (severity >= SeverityRanges.concern.min) {
		return SeverityLevel.CONCERN;
	}
	if (severity >= SeverityRanges.notable.min) {
		return SeverityLevel.NOTABLE;
	}
	return SeverityLevel.ROUTINE;
}

/**
 * Determines if a severity value is at or above the high-severity threshold
 * Used to determine if a measurement should trigger real-time alerting
 */
export function isHighSeverity(severity: number): boolean {
	return severity >= SEVERITY_THRESHOLD;
}

/**
 * Type guard to check if a number is a valid severity value (1-10)
 */
export function isValidSeverity(value: unknown): value is number {
	return (
		typeof value === "number" &&
		Number.isInteger(value) &&
		value >= SEVERITY_MIN &&
		value <= SEVERITY_MAX
	);
}
